import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles } from '@material-ui/core/styles';
import fileDownload from 'js-file-download';
import { getIn } from 'formik';
import {
  DynamicForm,
  ThreeBtn,
  FieldType,
  AlertDialog,
  Comments,
  Loader,
  Share,
  FileViewer,
} from '../../components';
import { useAuth } from '../../hooks/auth.hook';
import { useAlert } from '../../components/alert-info/use-alert';

import { ReportStatus } from '../../types/theme';
import { useStations } from '../../hooks/stations';
import { useRoutes, useApi } from '../../hooks';

const useStyles = makeStyles((theme) => ({
  infos: {
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

type CreateReportProps = {
  otherFields: { [key: string]: FieldType };
  themeShortCode: string;
  onChange?: (form: any) => void;
  initialValues?: any;
  validationSchema?: object;
};

export const CreateReport: React.FC<CreateReportProps> = ({
  otherFields,
  themeShortCode,
  initialValues,
  onChange,
  validationSchema,
}: CreateReportProps) => {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState<any>(null);
  const [error, setError] = useState('');
  const [images, setImages] = useState<any[]>([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [disableStations, setDisableStations] = useState(true);
  const [circulationDate, setCirculationDate] = useState(moment());
  const { auth } = useAuth();
  const { getPathByTheme, navigate } = useRoutes();
  const {
    createReport,
    updateReport,
    updateReportStatus,
    deleteReport,
    getAttachedPiece,
    getReport,
  } = useApi();
  const params: any = useParams();
  const classes = useStyles();
  const { show, AlertTypes } = useAlert();
  const [loading, setLoading] = useState(false);

  const theme = getPathByTheme(themeShortCode);
  const navigateToMainReports = navigate(theme?.path || '');

  const formInitialValue = useMemo(() => {
    const commonValues = {
      author: `${auth?.firstName} ${auth?.lastName}`,
      circulationDate: new Date(),
    };

    if (initialValues) {
      return {
        ...commonValues,
        ...initialValues,
      };
    }
    return commonValues;
  }, [initialValues, auth]);

  const disabled = report && report.reportStatus === 'Envoyé';

  const {
    handleOnDateChange,
    serviceNames,
    handleServiceNameChange,
    stationOptions,
    getStations,
  } = useStations();

  const handleClickOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOnChange = useCallback(
    (form) => {
      setCirculationDate(form.values.circulationDate);

      const serviceName = getIn(form.values, 'serviceName');
      const serviceNameNotSelected = !serviceName;

      if (serviceNameNotSelected !== disableStations) {
        setDisableStations(serviceNameNotSelected);
      }
      handleServiceNameChange(serviceName);
      if (onChange) {
        onChange(form);
      }
    },
    [handleServiceNameChange, onChange, disableStations]
  );

  const handleOnFileSelect = useCallback(
    async (file: any) => {
      try {
        const extensions = file?.name?.split('.') || [];
        const fileExtension: string = extensions[extensions.length - 1] || '';
        const isImg = ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension.toLowerCase());
        if (file.piece) {
          setImages([file.piece]);
        } else {
          if (isImg) {
            setImageLoading(true);
          }
          const response: any = await getAttachedPiece({
            reportId: report?.reportId,
            name: file.name,
          });

          const url = window.URL.createObjectURL(response as Blob);

          if (isImg) {
            setImages([url]);
            setImageLoading(false);
          } else {
            fileDownload(response, file?.name);
          }
        }
      } catch (error) {
        setImageLoading(false);
        show({
          message: "Impossible d'obtenir la pièce jointe",
          severity: AlertTypes.error,
        });
      }
      //setImages([file.piece]);
    },
    [report, getAttachedPiece, show, AlertTypes]
  );
  const getReportById = useCallback(async () => {
    try {
      const { reportId } = params;
      if (!reportId) return;
      setLoading(true);
      const response: any = await getReport({ reportId, themeShortCode });
      const [report] = response.reports;
      setReport({
        ...report,
        author: `${report.authorFirstName} ${report.authorLastName}`,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [params, themeShortCode, getReport]);

  const handleOnFileViewClose = useCallback(() => {
    setImages([]);
  }, []);

  const handleOnSubmit = useCallback(
    async (values) => {
      try {
        delete values.author;
        delete values.createdDate;
        delete values.createdDatTime;
        const { fieldReportsDto = {} } = values;
        const nbrAttachedPiece = ((fieldReportsDto.attachedPieces as any[]) || []).length;
        if (report) {
          await updateReport({
            ...values,
            themeShortCode,
            reportId: report?.reportId,
            fieldReportsDto: {
              products: Object.keys(values).filter(e => e !== "circulationDate" && e !== "fieldReportsDto" && e !== "formDisabled" && e !== "anomalyTypeShortCode" && e !== "anomalyShortCode" && e !== "reportStatus" && e !== "serviceName" && e !== "uicStationCode" && e !== "comment").map((val => {
                return {
                  name: val,
                  number: values?.[`${val}`]
                }
              })),
              ...fieldReportsDto,
              nbrAttachedPiece,
            },
          });

        } else {
          await createReport({
            ...values,
            themeShortCode,
            fieldReportsDto: {
              products: Object.keys(values).filter(e => e !== "circulationDate" && e !== "fieldReportsDto" && e !== "formDisabled" && e !== "anomalyTypeShortCode" && e !== "anomalyShortCode" && e !== "reportStatus" && e !== "serviceName" && e !== "uicStationCode" && e !== "comment").map((val => {
                return {
                  name: val,
                  number: typeof (values?.[`${val}`]) === 'number' ? values?.[`${val}`] : 0
                }
              })),
              ...fieldReportsDto,
              nbrAttachedPiece,
            },
          });
        }

        const message =
          values.reportStatus === ReportStatus.sent
            ? 'Le rapport est envoyé et transmis à ton manager.'
            : "Ton brouillon a été enregistré. Le rapport n'est pas transmis à ton manager.";
        show({ message, severity: AlertTypes.success });
        navigateToMainReports();
      } catch (error) {
        setError('report/submit-error');
      }
    },
    [themeShortCode, report, AlertTypes, createReport, navigateToMainReports, show, updateReport]
  );

  const handleOnDelete = useCallback(async () => {
    try {
      await deleteReport(report?.reportId);
      navigateToMainReports();
      show({ message: 'Le rapport a été supprimé', severity: AlertTypes.success });
    } catch (err) {
      show({ message: 'Suppression du rapport impossible', severity: AlertTypes.error });
    }
  }, [report, AlertTypes, deleteReport, show, navigateToMainReports]);

  useEffect(() => {
    const { reportId } = params;
    setLoading(true);
    const report = JSON.parse(localStorage.getItem('row') as string);
    if (reportId === report?.reportId) {
      if (report && report?.fieldReportsDto?.products?.length > 0) {
        setReport({
          ...report,
          ...report.fieldReportsDto.products.reduce(
            (obj: any, item: any) => Object.assign(obj, {
              [item?.name]: item?.number
            }), {}),
          author: `${report.authorFirstName} ${report.authorLastName}`,
        });
      }
      if (report) {
        setReport({
          ...report,
          author: `${report.authorFirstName} ${report.authorLastName}`,
        });
      }
      setLoading(false);
    } else {
      getReportById();
    }
  }, [getReportById, params]);

  useEffect(() => {
    getStations(circulationDate);
  }, [circulationDate, getStations]);

  useEffect(() => {
    (async () => {
      if (
        report &&
        report.reportId &&
        report.authorCPCode !== auth.userName &&
        report.userReportStatus &&
        report.userReportStatus < ReportStatus.seen
      ) {
        try {
          await updateReportStatus({
            reportId: report.reportId,
            reportStatus: 3,
            userName: auth.userName,
          });
        } catch (error) {
          show({
            message: 'Impossible de mettre à jour le statut du rapport',
            severity: AlertTypes.error,
          });
        }
      }
    })();
  }, [report, updateReportStatus, auth, AlertTypes, show]);

  return (
    <Loader loading={(!!params.reportId && !report) || loading}>
      <div>
        {report && (
          <div className={classes.infos}>
            <Typography color="textPrimary">
              Statut : <strong>{report?.reportStatus}</strong>
            </Typography>
            {disabled ? (
              <Share reportId={report.reportId} themeShortCode={themeShortCode} />
            ) : (
              <Button startIcon={<DeleteIcon />} color="secondary" onClick={handleClickOpen}>
                Supprimer
              </Button>
            )}
          </div>
        )}
        <DynamicForm
          initialValue={report || formInitialValue}
          disabled={disabled}
          fields={{
            author: {
              type: 'text',
              label: 'Agent',
              disabled: true,
            },
            createdDatTime: {
              type: 'date',
              label: 'Date de saisie',
              grid: 6,
              disabled: true,
            },
            circulationDate: {
              type: 'date',
              label: 'Date de circulation',
              grid: 6,
              initialValue: new Date(),
              onChange: handleOnDateChange,
            },
            serviceName: {
              type: 'autocomplete',
              label: 'Numero de train',
              grid: 6,
              options: serviceNames,
            },
            uicStationCode: {
              type: 'select',
              label: 'Gare',
              grid: 6,
              options: stationOptions,
              disabled: disableStations,
            },
            ...otherFields,
            'fieldReportsDto.attachedPieces': {
              type: 'file',
              label: 'Pièce jointe (Fichiers acceptés: JPEG, JPG, PNG)',
              onFileSelect: handleOnFileSelect,
              grid: 6,
            },
            'fieldReportsDto.getResponse': {
              type: 'switch',
              label: 'Demande de réponse',
            },
          }}
          onChange={handleOnChange}
          onSubmit={handleOnSubmit}
          validationSchema={validationSchema}
          error={error}
        >
          {(form: any) => {
            if (disabled) return null;

            return (
              <ThreeBtn
                primaryTitle="Envoyer"
                isPrimarySubmitting={form.isSubmitting}
                primaryOnClick={async () => {
                  await form.setFieldValue('reportStatus', ReportStatus.sent);
                  form.handleSubmit();
                }}
                secondaryTitle="Enregistrer et finir plus tard"
                isSecondarySubmitting={form.isSubmitting}
                secondaryOnClick={async () => {
                  await form.setFieldValue('reportStatus', ReportStatus.saved);
                  form.handleSubmit();
                }}
                disabled={form.isSubmitting}
              />
            );
          }}
        </DynamicForm>
        {disabled && <Comments reportId={report?.reportId} />}
        <AlertDialog
          title="Suppression d'un rapport"
          description=""
          open={open}
          onClose={handleClose}
          onConfirm={handleOnDelete}
        />
        <FileViewer
          open={!!images.length || imageLoading}
          images={images}
          onClose={handleOnFileViewClose}
          loading={imageLoading}
        />
      </div>
    </Loader>
  );
};

export default CreateReport;
